<template>
  <VeeForm as="div" ref="form" name="ReturnCaseForm" :validate-on-mount="true" :validation-schema="validationSchema">
    <div class="return-case-form">
      <div class="return-case-form-item">
        <SelectField
          :label="$t('viewCase.return_reason')"
          name="reason"
          :model-value="returnCase.reason"
          :options="defectReasonOptions"
          :placeholder="$t('global.select_placeholder')"
          :disabled="!isEditable"
          :required="isEditable"
          data-cy="defect-reason-dropdown"
          @update:modelValue="(val) => updateCaseField('reason', val)" />
        <BackstageInput
          :model-value="returnCase.parcelCount"
          :required="isEditable"
          name="parcelCount"
          :disabled="!isEditable"
          :label="$t('returnCaseForm.parcels')"
          type="number"
          @update:modelValue="(val) => updateCaseField('parcelCount', val as number)">
        </BackstageInput>
      </div>
      <div class="return-case-form-item">
        <BackstageInput
          :model-value="returnCase.description"
          type="textarea"
          :label="$t('viewCase.comments')"
          :required="isEditable"
          name="description"
          :class="{ disabled: !isEditable }"
          :disabled="!isEditable"
          @update:modelValue="(val) => updateCaseField('description', val as string)">
        </BackstageInput>
      </div>
    </div>
  </VeeForm>
</template>

<script lang="ts" setup>
import { Form as VeeForm } from 'vee-validate';
import { computed, ref, toRef, watch } from 'vue';

import BackstageInput from '@/components/common/BackstageInput.vue';
import SelectField, { type SelectFieldOption } from '@/components/common/SelectField.vue';
import { useFormValidation } from '@/composables/form-validation';
import { useCaseUpdater } from '@/composables/useCaseUpdater';
import type { ReturnCase } from '@/types/case.interface';

const props = defineProps<{
  isEditable: boolean;
  returnCase: ReturnCase;
  defectReasonOptions: SelectFieldOption[];
}>();

const emit = defineEmits<{
  'update-case': [returnCase: ReturnCase];
  'form-is-valid': [isValid: boolean];
}>();

const { textRequiredValidation, numberValidationRequiredMinMaxValue } = useFormValidation();
const { updateCaseField } = useCaseUpdater(toRef(props, 'returnCase'), (updatedCase) => onUpdateCase(updatedCase));

const form = ref<InstanceType<typeof VeeForm>>();

const validationSchema = computed(() => {
  return {
    reason: textRequiredValidation('return_reason'),
    parcelCount: numberValidationRequiredMinMaxValue('parcels', 1, 199),
    description: textRequiredValidation('comments'),
  };
});

watch(
  () => form.value?.meta?.valid,
  (isValid = false) => {
    emit('form-is-valid', isValid);
  },
  { immediate: true },
);

const onUpdateCase = (updatedCase: ReturnCase) => {
  emit('update-case', updatedCase);
};
</script>

<style scoped lang="scss">
.return-case-form {
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  .return-case-form-item {
    width: 100%;
    display: flex;
    gap: 12px;
    flex-direction: column;
  }
}
</style>
